import { useParams, Link } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { useVerifyEmail } from '@/hooks/useInviteRequest';

export function VerifyEmail() {
  const { token } = useParams<{ token: string }>();
  const verifyEmail = useVerifyEmail();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const hasVerified = useRef(false);

  useEffect(() => {
    if (hasVerified.current) return;

    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link');
      return;
    }

    hasVerified.current = true;
    verifyEmail.mutateAsync({ token })
      .then((result) => {
        setStatus('success');
        setMessage(result.message);
      })
      .catch((err) => {
        setStatus('error');
        setMessage(err instanceof Error ? err.message : 'Verification failed. Please try again.');
      });
  }, [token, verifyEmail]);

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-indigo-500 border-t-transparent" />
          <p className="mt-4 text-gray-400">Verifying your email...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950">
        <div className="mx-auto max-w-md rounded-lg border border-gray-700 bg-gray-800 p-8 text-center">
          <div className="mb-4 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500/20">
              <svg
                className="h-8 w-8 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                role="img"
                aria-label="Error"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white">Verification Failed</h1>
          <p className="mt-2 text-gray-400">{message}</p>
          <Link
            to="/"
            className="mt-6 inline-block text-indigo-400 hover:text-indigo-300"
          >
            Return to homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950">
      <div className="mx-auto max-w-md rounded-lg border border-gray-700 bg-gray-800 p-8 text-center">
        <div className="mb-4 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500/20">
            <svg
              className="h-8 w-8 text-green-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              role="img"
              aria-label="Success"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>
        <h1 className="text-2xl font-bold text-white">Email Verified!</h1>
        <p className="mt-2 text-gray-400">{message}</p>
        <Link
          to="/"
          className="mt-6 inline-block rounded-md bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-3 font-medium text-white hover:from-indigo-600 hover:to-purple-600"
        >
          Explore Podcasts
        </Link>
      </div>
    </div>
  );
}
